import { useEffect, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { InlineRowGrid, type InlineColumn } from "@components/input";
import { CatalogPicker } from "@components/catalog";
import { useMoney } from "@units/useMoney";
import { fmtNum } from "@lib/format";
import type { MudProductOut, CatalogValue } from "@api/types";

interface ProductDraft {
  product_entry_key: string | null;
  unit: string;
  qty: string;
  unit_cost: string;
}

const emptyDraft = (): ProductDraft => ({
  product_entry_key: null,
  unit: "",
  qty: "",
  unit_cost: "",
});

/**
 * Products section (§8) — the day's consumed-products grid. Product binds to the
 * Oxbo-M `mud_products` catalog via CatalogPicker (code stored, label shown);
 * qty × unit_cost is the extended line total (formatting-only, computed here).
 * A footer sums the daily mud cost. Committed rows are optimistic-local until
 * the write endpoint (bulk-upsert `mud_product`) is wired.
 */
export function MudProducts({
  products,
  productLabel,
  lineCost,
  dailyCost,
  currency,
  reportDate,
  readOnly = false,
}: {
  products: MudProductOut[];
  productLabel: (code: string) => string;
  lineCost: (p: MudProductOut) => number | null;
  dailyCost: number;
  currency: string;
  reportDate?: string;
  readOnly?: boolean;
}) {
  const { t, locale } = useLocale();
  const { money } = useMoney();

  const [added, setAdded] = useState<MudProductOut[]>([]);
  useEffect(() => setAdded([]), [products]);
  const rows = [...products, ...added];

  const columns: InlineColumn<MudProductOut, ProductDraft>[] = [
    {
      key: "product",
      header: t("mud.col.product"),
      render: (r) => (
        <span>
          {productLabel(r.product_entry_key)}
          <span className="mono dim" style={{ display: "block", fontSize: 11 }}>
            {r.product_entry_key}
          </span>
        </span>
      ),
      input: (d, patch) => (
        <CatalogPicker
          catalogKey="mud_products"
          value={d.product_entry_key}
          asOf={reportDate}
          compact
          placeholder={t("mud.col.product")}
          ariaLabel={t("mud.col.product")}
          onChange={(code, value: CatalogValue | null) =>
            patch({
              product_entry_key: code,
              // Seed the unit from the catalog value's declared UOM.
              unit: (value?.attrs?.uom as string | undefined) ?? d.unit,
            })
          }
        />
      ),
    },
    {
      key: "qty",
      header: t("mud.col.qty"),
      num: true,
      render: (r) => fmtNum(r.qty, locale, { maximumFractionDigits: 2 }),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={t("mud.col.qty")}
          value={d.qty}
          onChange={(e) => patch({ qty: e.target.value })}
        />
      ),
    },
    {
      key: "unit",
      header: t("mud.col.unit"),
      render: (r) => r.unit ?? t("common.na"),
      input: (d, patch) => (
        <input
          type="text"
          aria-label={t("mud.col.unit")}
          value={d.unit}
          onChange={(e) => patch({ unit: e.target.value })}
        />
      ),
    },
    {
      key: "unit_cost",
      header: t("mud.col.unitCost"),
      num: true,
      render: (r) =>
        r.unit_cost != null ? money(r.unit_cost, currency, { asOf: reportDate }) : t("common.na"),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={t("mud.col.unitCost")}
          value={d.unit_cost}
          onChange={(e) => patch({ unit_cost: e.target.value })}
        />
      ),
    },
    {
      key: "total",
      header: t("mud.col.total"),
      num: true,
      computed: true,
      render: (r) => {
        const c = lineCost(r);
        return c != null ? money(c, currency, { asOf: reportDate }) : t("common.na");
      },
    },
  ];

  const toRow = (d: ProductDraft): MudProductOut | null => {
    if (!d.product_entry_key || !d.qty.trim()) return null;
    const qty = Number(d.qty);
    const unit_cost = d.unit_cost.trim() ? Number(d.unit_cost) : null;
    return {
      id: `mp-local-${Date.now()}`,
      report_id: "",
      product_entry_key: d.product_entry_key,
      qty,
      unit: d.unit || null,
      unit_cost,
      cost: unit_cost != null ? qty * unit_cost : null,
      carry_over: false,
    };
  };

  return (
    <section className="card">
      <div className="panel-head-row">
        <h3>
          {t("mud.section.products")}{" "}
          <span className="dim" style={{ fontWeight: 400, fontSize: 12 }}>
            — {t("mud.section.products.note")}
          </span>
        </h3>
      </div>
      <InlineRowGrid<MudProductOut, ProductDraft>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        emptyDraft={emptyDraft}
        toRow={toRow}
        onCommit={(row) => setAdded((a) => [...a, row])}
        isCarried={(r) => r.carry_over}
        rowOwner={() => "p"}
        readOnly={readOnly}
        ariaLabel={t("mud.section.products")}
        emptyLabel={t("mud.noProducts")}
      />
      <div className="mud-total-row" data-owner="p">
        <span>{t("mud.totalDailyCost")}</span>
        <span className="mono tab-nums">{money(dailyCost, currency, { asOf: reportDate })}</span>
      </div>
    </section>
  );
}
